import type { UserType } from "@/generated/graphql"
import { useEffect, useMemo, useState } from "react"
import { createBranchReferralLink } from "@/stores/branch"
import { useShare } from "@/hooks/useShare"
import * as RewardsEvents from "@/analytics/events/rewards"
import { ScrollView, View } from "react-native"
import { H2 } from "@/components/standard/BFText"
import { PrimaryButton } from "@/components/buttons/CommonButtons"
import TextTabs, { TextTab } from "@/components/tabs/TextTabs"
import { RewardsTab } from "@/screens/rewards/old/RewardsTab"
import { EarningPointsTab } from "@/screens/rewards/old/EarningPointsTab"

interface ContentProps {
  user?: UserType
}

export const RewardsContent: React.FC<ContentProps> = ({
  user,
}: ContentProps) => {
  const [selectedTab, setSelectedTab] = useState("rewards")
  const referralLink = useMemo<string | null>(() => {
    return user ? createBranchReferralLink({ user }) : null
  }, [user])
  const share = useShare()

  const pointBalance = user?.lastPointTransaction?.balance || 0

  useEffect(() => {
    RewardsEvents.rewardsViewed({
      currentTab: selectedTab === "rewards" ? "rewards" : "earning points",
      pointBalance,
    })
  }, [selectedTab])

  const onRefer = () => {
    RewardsEvents.referAFriendClicked({
      currentTab: selectedTab === "rewards" ? "rewards" : "earning points",
      pointBalance,
    })
    void share({
      message: `Try Backflip to easily analyze investment properties, and to get funded on your flips!\n\nYou’ll get $100 off the closing costs of your first loan by joining with my referral link:\n\n${referralLink}`,
      title: "Do Flips!",
      subject: "$100 Towards Your Next Deal!",
    })
  }

  return (
    <View className="flex flex-col flex-1 bg-off-white">
      <View className="flex flex-row justify-between p-6 mb-4 shrink-0">
        <H2 className="text-midnight-blue">Backflip Rewards</H2>
        <View>
          <PrimaryButton
            testID={"refer-button"}
            title="Refer a Friend"
            size="sm"
            onPress={onRefer}
          />
        </View>
      </View>
      <TextTabs>
        <TextTab
          label="Rewards"
          value="rewards"
          selected={selectedTab === "rewards"}
          onSelect={setSelectedTab}
        />
        <TextTab
          label="Earning Points"
          value="points"
          selected={selectedTab === "points"}
          onSelect={setSelectedTab}
        />
      </TextTabs>
      {selectedTab === "rewards" ? (
        <RewardsTab />
      ) : (
        <ScrollView>
          <EarningPointsTab onRefer={onRefer} />
        </ScrollView>
      )}
    </View>
  )
}
